import type { AxiosResponse } from "axios";
import axios from "axios";
import type { WithPagination } from "../../../../types/types";
import type {
  ProductStatusType,
  ReferenceEntityConstraintSchema,
} from "../../../../types/types.PIM";

export type SellerTableProduct = {
  id: string;
  name: string;
  modified_at: { date: string; id: string };
  status: ProductStatusType;
  chemical_name: string;
  externalId: string;
  completion_score: {
    total: number;
    mandatory: number | null;
    show_mandatory: boolean;
    show_total: boolean;
  };
  pim_product_type?: "master" | "variant" | "standard";
  template_id?: string | null;
};

export type BuyerTableProduct = Omit<SellerTableProduct, "completion_score">;

type AppendToUrl = {
  existingParams: URLSearchParams;
  paramsToAppend: string[];
  key: string;
};

export function appendFiltersToUrl({
  existingParams,
  paramsToAppend,
  key,
}: AppendToUrl) {
  return paramsToAppend.forEach((param) => existingParams.append(key, param));
}

export type ApplyFiltersToURL = {
  active_tag_view?: string | null;
  q?: string;
  params: URLSearchParams;
  filtersFromUrl?: { [key: string]: string[] };
};

export const applyFiltersToURL = ({
  filtersFromUrl,
  params,
}: ApplyFiltersToURL): URLSearchParams => {
  if (filtersFromUrl) {
    Object.entries(filtersFromUrl)?.forEach((filterList) => {
      appendFiltersToUrl({
        existingParams: params,
        paramsToAppend: filterList[1],
        key: filterList[0],
      });
    });
  }
  return params;
};

export const get_all_condensed_products = async (
  tenant_id: string,
  params: URLSearchParams
) => {
  params.delete("offset");
  params.delete("limit");
  params.delete("perPage");
  const { data } = await axios.get<
    AxiosResponse<{ id: string; name: string }[]>
  >(`/v2/tenants/${tenant_id}/pim/products/condensed-summary?${params}`);
  return data.data;
};

export interface ConstructQuery<ProductType extends {}> {
  baseUrl: string;
  query: string;
  filtersFromUrl?: { [key: string]: string[] };
  previousPageData?: WithPagination<{ data: ProductType[] }> | null;
  index?: number;
  pageSize?: number;
}

/**
 * Given a list of reference entity constraints and an attribute ID,
 * this function returns all attribute IDs that are transitively related through
 * shared constraining entities. It starts with the entities of the given attribute,
 * then expands: whenever a constraint matches (shares an entity), its other
 * constrained_by_entities are added to the set, and filtering continues until
 * no new entities are found.
 *
 * This can be used to determine which other attributes might need to be affected
 * (e.g. cleared or updated) when a constraint involving the input attribute changes value.
 *
 * @param constraints - List of constraint objects, each describing reference entity logic.
 * @param attribute_id - ID of the attribute whose similar constraints are being sought.
 * @returns Array of attribute IDs that are constrained together with the given attribute.
 *
 * Example usage:
 *   getSimilarConstraints(constraints, "ATTRIBUTE_1")
 *     // returns ["ATTRIBUTE_1", "ATTRIBUTE_2", "ATTRIBUTE_3"] if they form a chain via shared entities.
 */
export const getSimilarConstraints = (
  constraints: ReferenceEntityConstraintSchema["constraints"],
  attribute_id?: string
) => {
  if (!attribute_id) {
    return [];
  }
  const initialConstraint = constraints.find(
    (c) => c.attribute_id === attribute_id
  );
  const initialEntities =
    initialConstraint?.constrained_by_entities?.map((e) => e.id) ?? [];
  if (initialEntities.length === 0) {
    return [];
  }

  // Build index: entityId -> constraints that contain this entity (for efficient BFS)
  type Constraint = ReferenceEntityConstraintSchema["constraints"][number];
  const entityToConstraints = new Map<string, Constraint[]>();
  for (const c of constraints) {
    for (const e of c.constrained_by_entities ?? []) {
      const list = entityToConstraints.get(e.id) ?? [];
      list.push(c);
      entityToConstraints.set(e.id, list);
    }
  }

  // BFS: expand constrainingEntities by following constraints that match
  const constrainingEntities = new Set(initialEntities);
  const queue = [...initialEntities];
  const seen = new Set(initialEntities);

  while (queue.length > 0) {
    const entityId = queue.shift()!;
    const relatedConstraints = entityToConstraints.get(entityId) ?? [];
    for (const c of relatedConstraints) {
      for (const e of c.constrained_by_entities ?? []) {
        if (!seen.has(e.id)) {
          seen.add(e.id);
          constrainingEntities.add(e.id);
          queue.push(e.id);
        }
      }
    }
  }

  return constraints
    .filter((c) =>
      c.constrained_by_entities?.some((e) => constrainingEntities.has(e.id))
    )
    .map((c) => c.attribute_id);
};

/**
 * Utility function to extract attribute selection entries from a given form field value.
 *
 * This function transforms a form field's value into an array of selection entry objects,
 * where each entry contains an `attribute_id` (the field key) and its corresponding `value`.
 * This is particularly useful for preparing data to be sent to APIs or stored in a normalized format.
 *
 * Logic:
 * - If the value is an array, each element is inspected:
 *    - If the element is an object with a `name` property, that name is used.
 *    - If the element is a string, it is used directly.
 *    - Otherwise, the element is ignored.
 * - If the value is an object with a `value` property, that value is used.
 * - If the value is a string, it is used directly.
 * - If the value is empty or falsy, an empty array is returned.
 *
 * @param key The attribute (field) key to use as "attribute_id" in return objects.
 * @param value The value associated with the form field, which can vary in type.
 * @returns An array of `{ attribute_id: string; value: string }` objects.
 */

export const getSelectionEntries = (
  key: string,
  value: any
): { attribute_id: string; value: string }[] => {
  const addEntry = (v: string) =>
    v && v !== "" ? [{ attribute_id: key, value: v }] : [];

  if (Array.isArray(value)) {
    return value.flatMap((v) => {
      if (typeof v === "object" && v?.name) {
        return addEntry(v.name);
      }
      if (typeof v === "string") {
        return addEntry(v);
      }
      return [];
    });
  }
  if (value && typeof value === "object" && value.value) {
    return addEntry(value.value);
  }
  if (typeof value === "string") {
    return addEntry(value);
  }
  return [];
};

/**
 * Utility function to extract selection entries for dependent attributes based on defined constraints.
 *
 * This function is used during editing to prepare a flattened array of objects representing the selected values
 * for each dependent attribute. It considers only those attributes that are listed as constraints, ensuring that
 * only relevant dependent selections are included.
 *
 * Logic:
 * - Iterates through each entry (key-value pair) in the provided `existing_values`.
 * - For each entry, checks if the attribute key is present in the constraints array.
 * - If the key is a constrained attribute, uses `getSelectionEntries` to transform the value into
 *   an array of `{ attribute_id: string; value: string }` objects.
 * - Returns a single flattened array of all the extracted selection entries.
 *
 * @param existing_values Record of attribute keys and their selected values in the current edit context.
 * @param constraints Array of constraint objects which specify dependent attributes.
 * @returns Array of selection entry objects for constrained attributes, in the form { attribute_id, value }.
 */
export const getEditConstraints = (
  existing_values: Record<string, any>,
  constraints: ReferenceEntityConstraintSchema["constraints"]
) => {
  return Object.entries(existing_values ?? {}).flatMap(([key, value]) => {
    const isDependent = constraints.some(
      (constraint) => constraint.attribute_id === key
    );
    if (isDependent) {
      return getSelectionEntries(key, value);
    }
    return [];
  });
};

export const getProductCountLabel = (
  total: number | undefined | null,
  t: (key: string) => string
): string => {
  const count = total ?? 0;
  return `${count} ${count === 1 ? t("Product") : t("products")}`;
};
